import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Trash,
    ArrowRight,
    Warning,
    CaretDown,
    CaretRight,
    ArrowsClockwise,
} from "@phosphor-icons/react";

function rag(score) {
    if (score == null) return "Amber";
    if (score < 40) return "Red";
    if (score < 70) return "Amber";
    return "Green";
}

function badge(status) {
    if (status === "ready") return null;
    const labels = {
        draft: { t: "Draft", c: "#9a9a9a" },
        awaiting_evidence: { t: "Awaiting evidence", c: "#d9a441" },
        generating: { t: "Generating…", c: "#2ec5b6" },
        failed: { t: "Failed", c: "#ff6b81" },
    };
    const m = labels[status];
    if (!m) return null;
    return (
        <span
            className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 border"
            style={{ color: m.c, borderColor: m.c + "80" }}
        >
            {m.t}
        </span>
    );
}

export default function Dashboard() {
    const { user, deleteAccount } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [expanded, setExpanded] = useState({}); // {series_id: true}
    const [cloning, setCloning] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/assessments");
            setItems(data);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const groups = useMemo(() => {
        if (!items) return null;
        // Group by series_id (fallback to id for legacy)
        const map = new Map();
        for (const it of items) {
            const sid = it.series_id || it.id;
            if (!map.has(sid)) map.set(sid, []);
            map.get(sid).push(it);
        }
        const out = [];
        for (const [sid, arr] of map.entries()) {
            // Sort descending by version_number then created_at
            arr.sort((a, b) => {
                const va = a.version_number || 1;
                const vb = b.version_number || 1;
                if (vb !== va) return vb - va;
                return new Date(b.created_at) - new Date(a.created_at);
            });
            out.push({
                series_id: sid,
                latest: arr[0],
                versions: arr,
            });
        }
        // Sort groups by latest version's created_at desc
        out.sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at));
        return out;
    }, [items]);

    const onDeleteAssessment = async (id) => {
        if (!window.confirm("Delete this assessment permanently?")) return;
        try {
            await api.delete(`/assessments/${id}`);
            load();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const onClone = async (it) => {
        if (
            !window.confirm(
                `Re-run "${it.organization_name}" as a new version? This copies the prior answers and evidence; you can edit before generating.`
            )
        )
            return;
        setCloning(it.id);
        setErr("");
        setMsg("");
        try {
            const { data } = await api.post(`/assessments/${it.id}/clone`);
            setMsg(
                `Created v${data.version_number} of "${data.organization_name}" — opening editor…`
            );
            nav(`/assessment?edit=${data.id}`);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setCloning(null);
        }
    };

    const onCancelStuck = async (it) => {
        if (
            !window.confirm(
                `Cancel this stuck "generating" report?\n\nIt will be marked as failed so you can delete it or start over. This does not refund the LLM call — it just unblocks the assessment.`
            )
        )
            return;
        try {
            await api.post(`/assessments/${it.id}/cancel`);
            setMsg(`Cancelled stuck generation for "${it.organization_name}".`);
            load();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const onDeleteAccount = async () => {
        await deleteAccount();
        window.location.href = "/";
    };

    const toggle = (sid) => setExpanded((e) => ({ ...e, [sid]: !e[sid] }));

    const renderRow = (it, { isVersionRow = false, isLatestInSeries = true, scanCount = 1 } = {}) => {
        const overall = it.scores?.overall ?? 0;
        const r = rag(overall);
        const v = it.version_number || 1;
        return (
            <div
                key={it.id}
                className={`p-5 grid grid-cols-12 gap-4 items-center ${isVersionRow ? "bg-[#0d0d0d] pl-10" : "bg-[#0a0a0a]"}`}
                data-testid={`assessment-row-${it.id}`}
            >
                <div className="col-span-12 md:col-span-5">
                    <div className="font-display text-lg flex items-center flex-wrap gap-2">
                        {isVersionRow && (
                            <span className="text-[#7a7a7a] mr-1 font-mono text-sm">
                                v{v}
                            </span>
                        )}
                        <span>{it.organization_name}</span>
                        {!isVersionRow && scanCount > 1 && (
                            <span
                                className="text-[12px] tracking-widest uppercase text-[#9a9a9a] border border-[#2a2a2a] px-2 py-0.5"
                                data-testid={`series-count-${it.series_id || it.id}`}
                            >
                                {scanCount} scans · latest v{v}
                            </span>
                        )}
                        {badge(it.status)}
                    </div>
                    <div className="text-xs text-[#c2c2c2] mt-1">
                        {it.industry} · {it.user_count} users ·{" "}
                        {new Date(it.created_at).toLocaleString()}
                        {it.parent_assessment_id && (
                            <span className="text-[#7a7a7a]"> · cloned</span>
                        )}
                    </div>
                </div>
                <div className="col-span-6 md:col-span-3">
                    <div className="font-mono text-3xl">
                        {overall}
                        <span className="text-base text-[#9a9a9a]">/100</span>
                    </div>
                    <div className="text-[13px] uppercase tracking-widest text-[#9a9a9a]">
                        Overall
                    </div>
                </div>
                <div className="col-span-6 md:col-span-2">
                    {it.status === "ready" && <span className={`rag-pill rag-${r}`}>{r}</span>}
                </div>
                <div className="col-span-12 md:col-span-2 flex md:justify-end gap-2 flex-wrap">
                    {it.status === "ready" && (
                        <Link
                            to={`/report/${it.id}`}
                            className="btn-secondary"
                            data-testid={`view-report-${it.id}`}
                        >
                            Open
                        </Link>
                    )}
                    {(it.status === "draft" || it.status === "awaiting_evidence") && (
                        <Link
                            to={`/assessment?edit=${it.id}`}
                            className="btn-secondary"
                            data-testid={`continue-draft-${it.id}`}
                        >
                            Continue
                        </Link>
                    )}
                    {it.status === "ready" && isLatestInSeries && (
                        <button
                            className="btn-ghost"
                            onClick={() => onClone(it)}
                            disabled={cloning === it.id}
                            title="Re-run as a new version (inherits prior answers + evidence)"
                            data-testid={`rerun-${it.id}`}
                        >
                            <ArrowsClockwise
                                size={16}
                                className={cloning === it.id ? "animate-spin" : ""}
                            />
                        </button>
                    )}
                    {it.status === "generating" && (
                        <button
                            className="btn-ghost"
                            onClick={() => onCancelStuck(it)}
                            title="Cancel stuck generation (available after 5 min)"
                            data-testid={`cancel-stuck-${it.id}`}
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        className="btn-ghost"
                        onClick={() => onDeleteAssessment(it.id)}
                        title="Delete assessment"
                        data-testid={`delete-assessment-${it.id}`}
                    >
                        <Trash size={16} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12" data-testid="dashboard-page">
            <div className="grid grid-cols-12 gap-8 items-start">
                <div className="col-span-12 md:col-span-8">
                    <div className="overline mb-3">Your dashboard</div>
                    <h1 className="font-display text-4xl md:text-5xl leading-tight">
                        Welcome, {user.name?.split(" ")[0] || "there"}.
                    </h1>
                    <p className="text-[#c2c2c2] mt-4 max-w-2xl leading-relaxed">
                        Your saved License Risk Scans. Start a new one, revisit a
                        report, re-run an existing scan as a new version, or delete data — your choice.
                    </p>
                    <div className="mt-8">
                        <Link
                            to="/assessment"
                            className="btn-primary inline-flex items-center gap-2"
                            data-testid="dashboard-new-scan-button"
                        >
                            Begin a new scan for a client <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                </div>
                <div className="col-span-12 md:col-span-4 border border-[#2a2a2a] bg-[#141414] p-5">
                    <div className="overline mb-3">Account</div>
                    <div className="text-sm text-[#d4d4d4] space-y-1">
                        <div>
                            <span className="text-[#9a9a9a] uppercase tracking-wider text-[13px] mr-2">
                                Email
                            </span>
                            {user.email}
                        </div>
                        {user.organization && (
                            <div>
                                <span className="text-[#9a9a9a] uppercase tracking-wider text-[13px] mr-2">
                                    Org
                                </span>
                                {user.organization}
                            </div>
                        )}
                    </div>
                    <div className="mt-5 pt-5 border-t border-[#2a2a2a]">
                        <div className="overline mb-2 !text-[#ff6b81]">Erasure</div>
                        <p className="text-xs text-[#c2c2c2] leading-relaxed">
                            Permanently delete your account and every assessment you
                            have submitted.
                        </p>
                        {!confirmDelete ? (
                            <button
                                className="btn-danger mt-4 w-full inline-flex items-center justify-center gap-2"
                                onClick={() => setConfirmDelete(true)}
                                data-testid="delete-account-init"
                            >
                                <Trash size={14} weight="bold" /> Delete my account
                            </button>
                        ) : (
                            <div className="mt-3 space-y-2">
                                <div className="block-risk text-xs inline-flex items-start gap-2">
                                    <Warning size={14} className="mt-[2px]" />
                                    This action is irreversible.
                                </div>
                                <button
                                    className="btn-danger w-full"
                                    onClick={onDeleteAccount}
                                    data-testid="delete-account-confirm"
                                >
                                    Yes, delete everything
                                </button>
                                <button
                                    className="btn-secondary w-full"
                                    onClick={() => setConfirmDelete(false)}
                                    data-testid="delete-account-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {msg && (
                <div className="block-opportunity mt-8" data-testid="dashboard-msg">
                    {msg}
                </div>
            )}
            {err && (
                <div className="block-risk mt-8" data-testid="dashboard-error">
                    {err}
                </div>
            )}

            <div className="mt-14">
                <div className="overline mb-4">Your assessments</div>
                {groups === null ? (
                    <div className="text-[#c2c2c2]">Loading…</div>
                ) : groups.length === 0 ? (
                    <div className="border border-[#2a2a2a] bg-[#141414] p-10 text-center" data-testid="dashboard-empty">
                        <div className="font-display text-2xl">No scans yet</div>
                        <p className="text-[#c2c2c2] mt-2">
                            Start your first License Risk Scan to receive an
                            executive report.
                        </p>
                        <Link to="/assessment" className="btn-primary mt-6 inline-block">
                            Begin
                        </Link>
                    </div>
                ) : (
                    <div className="border border-[#2a2a2a] divide-y divide-[#2a2a2a]">
                        {groups.map((g) => {
                            const hasVersions = g.versions.length > 1;
                            const isOpen = !!expanded[g.series_id];
                            return (
                                <div key={g.series_id} data-testid={`series-${g.series_id}`}>
                                    <div className="flex">
                                        {hasVersions && (
                                            <button
                                                type="button"
                                                onClick={() => toggle(g.series_id)}
                                                className="px-3 hover:bg-[#1a1a1a] text-[#9a9a9a] flex items-center justify-center w-10 border-r border-[#2a2a2a]"
                                                title={isOpen ? "Collapse versions" : "Expand versions"}
                                                data-testid={`series-toggle-${g.series_id}`}
                                            >
                                                {isOpen ? (
                                                    <CaretDown size={14} weight="bold" />
                                                ) : (
                                                    <CaretRight size={14} weight="bold" />
                                                )}
                                            </button>
                                        )}
                                        <div className="flex-1">
                                            {renderRow(g.latest, {
                                                isLatestInSeries: true,
                                                scanCount: g.versions.length,
                                            })}
                                        </div>
                                    </div>
                                    {hasVersions && isOpen && (
                                        <div className="border-t border-[#2a2a2a]">
                                            {g.versions.slice(1).map((v) => (
                                                <div
                                                    key={v.id}
                                                    className="border-b border-[#2a2a2a] last:border-b-0"
                                                >
                                                    {renderRow(v, {
                                                        isVersionRow: true,
                                                        isLatestInSeries: false,
                                                        scanCount: g.versions.length,
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
